/**
 * dashboardWidgetData — data layer for the D2 dashboard widgets (2026-07-17).
 *
 * Replaces the old "download 10 collections, compute everything" dashboard.
 * Each widget fetches ONLY what it renders, server-side bounded, cached for
 * DASHBOARD_TTL_MS (2h). All reads are counted in readTelemetry.
 *
 * Widgets served:
 *  - Sales Overview  → getSalesOverview(companyId, fy, period, branchId)
 *      GRAPH ONLY, pure server-side sum()/count() aggregations per bucket
 *      (~5 reads per bucket instead of downloading every sale document).
 *  - Sales Overview top-5 → getSalesOverviewBreakdown(companyId, fy, period, branchId)
 *      The expensive line-level fetch, kept OUT of the dashboard load: it runs
 *      only when the user actually opens a Top Items / Customers / Regions tab
 *      (aggregations can't group by item/customer/region).
 *  - Sales vs Recovery → getSalesVsRecovery(companyId, fy, branchId)
 *      Pure server-side sum() aggregations per month (~2 reads per bucket).
 *  - Sales vs Expense → getSalesVsExpense(companyId, fy, branchId)
 *      Sales via sum() per month; expense via sum(debitAmount) on
 *      subTransactions per (docMonth == bucket, accountCode 52xxx-53xxx range,
 *      i.e. operating + financial expenses, NOT cost of sales) —
 *      requires the docMonth field (stamped on new rows; backfilled once) and
 *      the (docMonth, accountCode) composite index.
 *  - Activity feed → getRecentTransactions(companyId, limit 10)
 */

import {
  collection, query, where, orderBy, limit, getDocs,
  getAggregateFromServer, sum, count
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getTransactions } from './transactionHelper';
import { getCached, invalidateCache } from './fyDataCache';
import { DASHBOARD_TTL_MS } from './dashboardHelper';
import { recordReads } from './readTelemetry';

const SALE_TYPES = ['SI', 'NSI', 'PS'];
const RETURN_TYPES = ['SR', 'NSR'];
const RECOVERY_TYPES = ['CR', 'BR'];

// A document counts toward revenue ONLY in these statuses (Draft / Cancelled
// must never show up in the graph). The aggregation path and the document path
// use this same list so both produce identical figures.
const POSTED_STATUSES = ['Posted', 'Partial', 'Paid', 'Completed'];

const toYMD = (v) => {
  if (!v) return null;
  if (typeof v === 'string') return v.slice(0, 10);
  if (v.toDate) v = v.toDate();
  if (v instanceof Date) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  return null;
};

const todayYMD = () => toYMD(new Date());

// Period → { fromDate, toDate, buckets: [{key,label,from,to}] }
// day   = last 30 days, daily buckets
// week  = last 12 weeks, weekly buckets
// month = current FY (bounded to today), monthly buckets
// year  = last up-to-5 FYs is overkill — we chart the current FY monthly and
//         label it yearly totals; period 'year' = whole FY as one series of months.
export const buildPeriodWindow = (period, fy) => {
  const today = new Date();
  const buckets = [];
  const push = (from, to, label) => buckets.push({ key: from, label, from, to });

  if (period === 'day') {
    for (let i = 29; i >= 0; i -= 1) {
      const d = new Date(today); d.setDate(d.getDate() - i);
      const ymd = toYMD(d);
      push(ymd, ymd, `${d.getDate()}/${d.getMonth() + 1}`);
    }
  } else if (period === 'week') {
    for (let i = 11; i >= 0; i -= 1) {
      const end = new Date(today); end.setDate(end.getDate() - i * 7);
      const start = new Date(end); start.setDate(start.getDate() - 6);
      push(toYMD(start), toYMD(end), `${start.getDate()}/${start.getMonth() + 1}`);
    }
  } else if (period === 'month') {
    // month: last 6 calendar months, monthly buckets (rolling window)
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const y = d.getFullYear(); const m = d.getMonth();
      const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      let to = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      if (to > todayYMD()) to = todayYMD();
      push(from, to, d.toLocaleString('en', { month: 'short' }));
    }
  } else {
    // year: monthly buckets across the active FY (up to today)
    const fyStart = toYMD(fy?.startDate) || `${today.getFullYear()}-01-01`;
    const fyEnd = toYMD(fy?.endDate) || todayYMD();
    const end = fyEnd < todayYMD() ? fyEnd : todayYMD();
    let cursor = new Date(fyStart.slice(0, 7) + '-01T00:00:00');
    const endDate = new Date(end + 'T00:00:00');
    while (cursor <= endDate) {
      const y = cursor.getFullYear();
      const m = cursor.getMonth();
      const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      let to = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      if (from < fyStart) { /* keep aligned to FY start month */ }
      if (to > end) to = end;
      push(from, to, cursor.toLocaleString('en', { month: 'short' }));
      cursor = new Date(y, m + 1, 1);
    }
  }
  return {
    fromDate: buckets[0]?.from || todayYMD(),
    toDate: buckets[buckets.length - 1]?.to || todayYMD(),
    buckets
  };
};

/**
 * ONE server-side aggregation over the `tranTypeCode` docs in a date range.
 * Returns { total: Σfield, count: matching docs } — the count rides along on the
 * SAME query, so it costs nothing extra.
 *
 * `statuses` (optional) adds where('status','in',[...]). WITHOUT it the
 * aggregation counts EVERY status, Draft and Cancelled included — that is the
 * long-standing behaviour of sumForType below and must not change for the
 * widgets that already use it.
 *
 * Index note: sum() aggregations need a composite index that ENDS with the
 * summed field (see firestore.indexes.json — the totalAmount / balanceAmount
 * tails). Adding `status` to the filter therefore needs its own index.
 */
const aggregateForType = async (companyId, tranTypeCode, fromDate, toDate, branchId, opts = {}) => {
  const { field = 'totalAmount', statuses = null, withCount = false } = opts;
  const ref = collection(db, 'companies', companyId, 'transactions');
  const conds = [
    where('tranTypeCode', '==', tranTypeCode),
    where('docDate', '>=', fromDate),
    where('docDate', '<=', toDate)
  ];
  if (branchId) conds.push(where('branchId', '==', branchId));
  if (statuses && statuses.length) conds.push(where('status', 'in', statuses));
  const spec = withCount ? { total: sum(field), docs: count() } : { total: sum(field) };
  const snap = await getAggregateFromServer(query(ref, ...conds), spec);
  recordReads(companyId, 1);
  const d = snap.data();
  return { total: d.total || 0, count: withCount ? (d.docs || 0) : 0 };
};

// One sum() aggregation: total of `field` for docs of `tranTypeCode` in range.
// (Unchanged behaviour — no status filter — for Sales vs Recovery / Expense.)
const sumForType = async (companyId, tranTypeCode, fromDate, toDate, branchId, field = 'totalAmount') =>
  (await aggregateForType(companyId, tranTypeCode, fromDate, toDate, branchId, { field })).total;

// ── Sales Overview: shared document-level fetch (the EXPENSIVE path) ─────────
// Downloads whole sale documents for the window. Used by the Top-5 breakdown
// and by the aggregation fallback. `includeReturns` is off for the breakdown —
// the top-5 lists are built from sales only, exactly as before.
const fetchSalesDocs = async (companyId, fy, period, branchId, includeReturns = true) => {
  const { fromDate, toDate, buckets } = buildPeriodWindow(period, fy);
  const fetchType = (code) => getTransactions(companyId, {
    tranTypeCode: code, fromDate, toDate, ...(branchId ? { branchId } : {})
  });
  const types = includeReturns ? [...SALE_TYPES, ...RETURN_TYPES] : [...SALE_TYPES];
  const fetched = await Promise.all(types.map(fetchType));
  const flat = fetched.flat();
  const isPosted = (t) => POSTED_STATUSES.includes(t.status);
  const sales = flat.filter(t => SALE_TYPES.includes(t.tranTypeCode) && isPosted(t));
  const returns = flat.filter(t => RETURN_TYPES.includes(t.tranTypeCode) && isPosted(t));
  return { fromDate, toDate, buckets, sales, returns };
};

// Top 5 items / customers / regions from line-level data (unchanged math).
const buildTopLists = (sales) => {
  const itemTotals = {}; const customerTotals = {}; const regionTotals = {};
  sales.forEach(t => {
    const cKey = t.partyName || 'Walk-in';
    customerTotals[cKey] = (customerTotals[cKey] || 0) + (t.totalAmount || 0);
    const rKey = t.regionName || t.region || 'Unassigned';
    regionTotals[rKey] = (regionTotals[rKey] || 0) + (t.totalAmount || 0);
    (t.lineItems || t.lines || []).forEach(l => {
      const iKey = l.itemName || l.description || 'Unknown item';
      itemTotals[iKey] = (itemTotals[iKey] || 0) + (l.totalAmount || l.lineTotal || l.amount || 0);
    });
  });
  const top = (obj) => Object.entries(obj)
    .sort((a, b) => b[1] - a[1]).slice(0, 5)
    .map(([name, value]) => ({ name, value: Math.round(value) }));
  return {
    topItems: top(itemTotals),
    topCustomers: top(customerTotals),
    topRegions: top(regionTotals)
  };
};

// The ORIGINAL (pre-aggregation) computation, kept as the safety net for when
// the aggregation query can't run — identical numbers, old cost.
const computeSalesOverviewFromDocs = async (companyId, fy, period, branchId) => {
  const { fromDate, toDate, buckets, sales, returns } =
    await fetchSalesDocs(companyId, fy, period, branchId, true);
  const series = buckets.map(b => {
    const inB = (t) => t.docDate >= b.from && t.docDate <= b.to;
    const gross = sales.filter(inB).reduce((s, t) => s + (t.totalAmount || 0), 0);
    const ret = returns.filter(inB).reduce((s, t) => s + (t.totalAmount || 0), 0);
    return { label: b.label, revenue: Math.round(gross - ret), invoices: sales.filter(inB).length };
  });
  return {
    series,
    totalRevenue: series.reduce((s, b) => s + b.revenue, 0),
    invoiceCount: sales.length,
    ...buildTopLists(sales),
    fromDate, toDate
  };
};

/**
 * SALES OVERVIEW (graph) — server-side aggregations only.
 * Per bucket: sum(totalAmount) + count() for SI/NSI/PS/SR/NSR, filtered to the
 * posted statuses, netted (sales − returns). ~5 aggregation reads per bucket
 * instead of downloading every sale document in the window (~15,000 docs).
 *
 * The Top Items / Customers / Regions lists are NOT included — they need
 * line-level data, so they are fetched lazily by getSalesOverviewBreakdown()
 * when the user opens one of those tabs. `breakdownLoaded: false` tells the
 * widget it still has to ask for them.
 */
export const getSalesOverview = async (companyId, fy, period, branchId = null) => {
  const key = `dashRaw|${companyId}|salesOverview|${period}|${branchId || 'all'}|${toYMD(fy?.startDate) || 'nofy'}`;
  return getCached(key, async () => {
    const { fromDate, toDate, buckets } = buildPeriodWindow(period, fy);
    try {
      const series = await Promise.all(buckets.map(async (b) => {
        const parts = await Promise.all(
          [...SALE_TYPES, ...RETURN_TYPES].map(c => aggregateForType(
            companyId, c, b.from, b.to, branchId,
            { statuses: POSTED_STATUSES, withCount: true }
          ))
        );
        const gross = parts[0].total + parts[1].total + parts[2].total;
        const ret = parts[3].total + parts[4].total;
        const invoices = parts[0].count + parts[1].count + parts[2].count;
        return { label: b.label, revenue: Math.round(gross - ret), invoices };
      }));
      return {
        series,
        totalRevenue: series.reduce((s, b) => s + b.revenue, 0),
        invoiceCount: series.reduce((s, b) => s + b.invoices, 0),
        breakdownLoaded: false,
        fromDate, toDate
      };
    } catch (e) {
      // Aggregation unavailable (composite index still building / not deployed).
      // Showing 0 would be a WRONG number, so fall back to the original
      // document scan — same figures as before, and it fills the top-5 lists
      // in the same pass (breakdownLoaded: true → widget skips the lazy call).
      console.warn('[salesOverview] aggregation unavailable, using document scan:', e?.message);
      const legacy = await computeSalesOverviewFromDocs(companyId, fy, period, branchId);
      return { ...legacy, breakdownLoaded: true };
    }
  }, DASHBOARD_TTL_MS);
};

/**
 * SALES OVERVIEW — Top Items / Customers / Regions.
 * The line-level download the graph no longer pays for. Called ONLY when the
 * user opens one of the three tabs, cached under its own key for the same TTL
 * (so switching tabs, or coming back within the TTL, costs 0 reads).
 */
export const getSalesOverviewBreakdown = async (companyId, fy, period, branchId = null) => {
  const key = `dashRaw|${companyId}|salesOverview|${period}|${branchId || 'all'}|${toYMD(fy?.startDate) || 'nofy'}|breakdown`;
  return getCached(key, async () => {
    const { sales } = await fetchSalesDocs(companyId, fy, period, branchId, false);
    return buildTopLists(sales);
  }, DASHBOARD_TTL_MS);
};

/**
 * SALES vs RECOVERY — monthly, pure aggregations (~4 reads per bucket).
 * Recovery = CR + BR receipts. Sales = SI+NSI+PS minus SR+NSR.
 */
export const getSalesVsRecovery = async (companyId, fy, branchId = null) => {
  const key = `dashRaw|${companyId}|salesRecovery|${branchId || 'all'}|${toYMD(fy?.startDate) || 'nofy'}`;
  return getCached(key, async () => {
    const { buckets } = buildPeriodWindow('month', fy);
    const series = await Promise.all(buckets.map(async (b) => {
      const [siT, nsiT, psT, srT, nsrT, crT, brT] = await Promise.all([
        ...SALE_TYPES.map(c => sumForType(companyId, c, b.from, b.to, branchId)),
        ...RETURN_TYPES.map(c => sumForType(companyId, c, b.from, b.to, branchId)),
        ...RECOVERY_TYPES.map(c => sumForType(companyId, c, b.from, b.to, branchId))
      ]);
      return {
        label: b.label,
        sales: Math.round(siT + nsiT + psT - srT - nsrT),
        recovery: Math.round(crT + brT)
      };
    }));
    const totalSales = series.reduce((s, b) => s + b.sales, 0);
    const totalRecovery = series.reduce((s, b) => s + b.recovery, 0);
    return {
      series,
      totalSales,
      totalRecovery,
      recoveryPct: totalSales > 0 ? Math.round((totalRecovery / totalSales) * 100) : 0
    };
  }, DASHBOARD_TTL_MS);
};

/**
 * SALES vs EXPENSE — monthly. Sales via aggregations; expense via
 * sum(debitAmount) - sum(creditAmount) over 5xxxx ledger rows per month using
 * the docMonth equality field (stamped on new rows + one-time backfill).
 * Months without backfilled rows read as 0 — the widget notes this.
 */
export const getSalesVsExpense = async (companyId, fy, branchId = null) => {
  const key = `dashRaw|${companyId}|salesExpense|${branchId || 'all'}|${toYMD(fy?.startDate) || 'nofy'}`;
  const result = await getCached(key, async () => {
    const { buckets } = buildPeriodWindow('month', fy);
    const subRef = collection(db, 'companies', companyId, 'subTransactions');
    // Set when an expense aggregation fails, so a broken read is never mistaken
    // for "this company has no expenses" — see the note where it is used below.
    let expenseFailed = false;

    const series = await Promise.all(buckets.map(async (b) => {
      const month = b.from.slice(0, 7);
      const salesParts = await Promise.all([
        ...SALE_TYPES.map(c => sumForType(companyId, c, b.from, b.to, branchId)),
        ...RETURN_TYPES.map(c => sumForType(companyId, c, b.from, b.to, branchId))
      ]);
      const salesTotal = salesParts[0] + salesParts[1] + salesParts[2] - salesParts[3] - salesParts[4];

      let expense = 0;
      try {
        // Operating (52) + Financial (53) expenses only — Cost of Sales (51) is
        // DELIBERATELY excluded (owner decision 2026-08-13). COGS moves with sales
        // volume, so including it made the expense bar shadow the sales bar and
        // hid what the chart is actually for: watching running costs against
        // revenue. Starting the range at '52' also picks up any future 54+ group.
        const conds = [
          where('docMonth', '==', month),
          where('accountCode', '>=', '52'),
          where('accountCode', '<', '6')
        ];
        const snap = await getAggregateFromServer(query(subRef, ...conds), {
          dr: sum('debitAmount'), cr: sum('creditAmount')
        });
        recordReads(companyId, 1);
        expense = (snap.data().dr || 0) - (snap.data().cr || 0);
      } catch (e) {
        // NEVER fail silently here. This used to swallow the error and return
        // 0, which the chart then displayed as "no expenses" — indistinguishable
        // from a real zero. A missing index (failed-precondition) hid behind
        // that for days.
        expenseFailed = true;
        console.error(
          `[SalesVsExpense] expense aggregation failed for ${b.label} — showing 0. ` +
          `Usually a missing index on subTransactions (docMonth, accountCode, ` +
          `creditAmount, debitAmount — summed fields go in ALPHABETICAL order, ` +
          `credit before debit) or rows without docMonth.`, e
        );
      }

      return { label: b.label, sales: Math.round(salesTotal), expense: Math.round(expense) };
    }));

    return {
      series,
      totalSales: series.reduce((s, b) => s + b.sales, 0),
      totalExpense: series.reduce((s, b) => s + b.expense, 0),
      expenseFailed,
    };
  }, DASHBOARD_TTL_MS);

  // A failed expense read must NOT sit in the cache for the full 2 hours. It
  // did exactly that: while the index was still building, every bucket threw,
  // the widget stored "expense = 0", and the chart kept showing that stale zero
  // long after the index went live — so the fix looked like it hadn't worked.
  // Dropping the entry means the next dashboard load retries instead.
  if (result?.expenseFailed) invalidateCache(key);
  return result;
};

/** ACTIVITY FEED — last 10 transactions, one tiny query. Company-wide on
 *  purpose: a branch filter + orderBy would need a new composite index. */
export const getRecentTransactions = async (companyId) => {
  const key = `dashRaw|${companyId}|recentTx`;
  return getCached(key, async () => {
    const ref = collection(db, 'companies', companyId, 'transactions');
    const snap = await getDocs(query(ref, orderBy('createdAt', 'desc'), limit(10)));
    recordReads(companyId, snap.size);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }, DASHBOARD_TTL_MS);
};
